<script setup>
import { computed } from 'vue'
import AppSkeleton from '@/components/atoms/AppSkeleton'
import HelpHubLinkCard from './HelpHubLinkCard.vue'
import HelpHubState from './HelpHubState.vue'
import { useStrapiStore } from '@/stores/StrapiStore'
import { useGetStarted } from '@/hooks/useGetStarted'
import { useHelpHub } from '@/hooks/useHelpHub'
import { HELP_HUB_TOPICS, useHelpTopics } from '@/hooks/useHelpTopics'
import { getStartedEntries, whatsNewEntries, helpTopicEntries, searchHelp } from '@/helpers/helpHubSearch'
import { safeCapture } from '@/helpers/posthog'
import { POSTHOG_EVENTS, HELP_HUB_CATEGORIES, HELP_HUB_SEARCH_SOURCE } from '@/constants'

const props = defineProps({
  query: { type: String, required: true },
})

const emit = defineEmits(['clear'])

// How a source's rows read (Figma 29871:320227): the badge over the title, and no marker.
const SOURCES = {
  [HELP_HUB_SEARCH_SOURCE.GET_STARTED]: { badge: 'Get started', badgeVariant: 'tint-light' },
  [HELP_HUB_SEARCH_SOURCE.WHATS_NEW]: { badge: 'What’s new', badgeVariant: 'purple' },
  [HELP_HUB_SEARCH_SOURCE.HELP_TOPICS]: { badge: 'Help topics', badgeVariant: 'gray' },
}

const strapiStore = useStrapiStore()
const { steps } = useGetStarted()
const { helpLink } = useHelpHub()
const { control } = useHelpTopics()

// The releases load with the What's new row; a search typed before it mounted asks for them here.
if (!strapiStore.changeLog.length && !strapiStore.changeLogLoading) strapiStore.fetchChangeLogData()

// Everything the Help tab lists, in its order: the steps and the pages under them, the releases, then the topic rows and the categories.
const entries = computed(() => [
  ...getStartedEntries(steps.value),
  ...whatsNewEntries(strapiStore.changeLog),
  ...helpTopicEntries(HELP_HUB_TOPICS, HELP_HUB_CATEGORIES),
])
const results = computed(() => searchHelp(props.query, entries.value))
// Steps or releases still on their way can add to the list.
const loading = computed(() => strapiStore.getStartedLoading || strapiStore.changeLogLoading)

// What makes a row a control: a hub page, the release on the changelog page, or the topic's own target.
const controlOf = (result) => {
  if (result.source === HELP_HUB_SEARCH_SOURCE.GET_STARTED) return { as: 'div', to: helpLink(result.slug) }
  if (result.source === HELP_HUB_SEARCH_SOURCE.WHATS_NEW) {
    return { as: 'div', to: `/changelog#post-${result.id}`, onClick: () => safeCapture(POSTHOG_EVENTS.WHATS_NEW_CLICKED, { item_id: result.id }) }
  }
  return control(result.topic)
}
</script>

<template>
  <!-- The matches, one flat list where the sections were; pt-2 under the field's pb-3 makes the frame's 20px. -->
  <section v-if="results.length" class="flex flex-col gap-y-4 px-4 pt-2 pb-5" aria-label="Search results">
    <p class="sr-only" aria-live="polite">{{ results.length }} {{ results.length === 1 ? 'result' : 'results' }}</p>

    <ul class="flex flex-col gap-y-4">
      <li v-for="result in results" :key="`${result.source}:${result.key}`">
        <HelpHubLinkCard
          v-bind="{ ...SOURCES[result.source], ...controlOf(result) }"
          :icon="null"
          :title="result.title"
          :description="result.description"
        />
      </li>
    </ul>
  </section>

  <!-- Nothing matched yet while the steps or the releases load: three rows' shapes. -->
  <div v-else-if="loading" role="status" class="flex flex-col gap-y-4 px-4 pt-2 pb-5">
    <span class="sr-only">Searching</span>
    <AppSkeleton v-for="row in 3" :key="row" height="h-[98px]" rounded="rounded-xl" />
  </div>

  <!-- No frame draws this one: the "Content not found" composition, with the search's own words. -->
  <HelpHubState
    v-else
    icon="heroicons-outline:search"
    title="No results found"
    :description="`Nothing in the Help Hub matches “${query.trim()}”. Try another word, or ask us in Chat.`"
    action-label="Clear search"
    action-icon="heroicons-outline:x"
    @action="emit('clear')"
  />
</template>
